// Canonical venda (sale) status — Phase 1.4 SSOT.
//
// The user sees THREE states: Confirmada (green), Pendente (amber), Cancelada
// (red) — mais um recorte da última: "Tentativa falhada", para a venda que
// nunca chegou a existir na Servopa (cancel_kind = 'nao_confirmada'). Chamar
// isso de "Cancelada" é dizer que houve algo a cancelar; não houve, o
// formulário não terminou e o cliente nunca teve o que pagar. Continua sendo
// vermelho e continua sendo kind "cancelled": só o rótulo diz a verdade.
//
// `reconciled` is an INTERNAL bookkeeping state (a
// platform-generated pending sale that also shows as pending in Servopa) — to
// the user it is just "Pendente". Previously each screen had its own drifting
// map (reconciled shown as "Reconciliada" in blue here, amber there, "Pendente"
// elsewhere; inconsistent gender "Confirmado/Confirmada"). Use saleStatusBadge()
// everywhere a venda status is rendered.

export type SaleStatusKind = "confirmed" | "pending" | "cancelled";

export type SaleStatusBadge = {
  kind: SaleStatusKind;
  label: "Confirmada" | "Pendente" | "Cancelada" | "Tentativa falhada" | "Anulada";
  /** Tailwind badge classes (background + text). */
  tone: string;
};

const CONFIRMED: SaleStatusBadge = {
  kind: "confirmed",
  label: "Confirmada",
  tone: "bg-green-100 text-green-800",
};
const PENDING: SaleStatusBadge = {
  kind: "pending",
  label: "Pendente",
  tone: "bg-amber-100 text-amber-800",
};
const CANCELLED: SaleStatusBadge = {
  kind: "cancelled",
  label: "Cancelada",
  tone: "bg-red-100 text-red-800",
};
const TENTATIVA_FALHADA: SaleStatusBadge = {
  kind: "cancelled",
  label: "Tentativa falhada",
  tone: "bg-red-100 text-red-800",
};

// Anulada pelo super admin (migration 235): a Servopa ainda fala dela, mas não
// vale. Cinza, e kind "cancelled" — fora da carteira como uma cancelada.
const ANULADA: SaleStatusBadge = {
  kind: "cancelled",
  label: "Anulada",
  tone: "bg-slate-200 text-slate-700",
};

// Every raw status collapses to one of the three user-facing badges.
const BY_STATUS: Record<string, SaleStatusBadge> = {
  confirmed: CONFIRMED,
  success: CONFIRMED,
  pending: PENDING,
  reconciled: PENDING, // internal — shown as pending to the user
  started: PENDING,
  cancelled: CANCELLED,
  failed: CANCELLED,
  anulada: ANULADA,
};

/**
 * Maps any raw venda status to a user-facing badge.
 *
 * `cancelKind` é opcional e só muda o rótulo do cancelamento: quem passa a
 * coluna diferencia a venda que o cliente largou ('nao_confirmada') da que era
 * um contrato pagando e parou ('churn'). Quem não passa continua vendo
 * "Cancelada", como antes.
 *
 * Unknown / missing status defaults to Pendente.
 */
export function saleStatusBadge(
  status: string | null | undefined,
  cancelKind?: string | null,
): SaleStatusBadge {
  const badge = BY_STATUS[(status ?? "").toLowerCase()] ?? PENDING;
  if (badge.kind === "cancelled" && cancelKind === "nao_confirmada") return TENTATIVA_FALHADA;
  return badge;
}
